import { useParams, useNavigate } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { useQuery } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAuthor } from "@/hooks/useAuthor";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { containsGM } from "@/lib/gmDetection";
import type { NostrEvent } from "@nostrify/nostrify";
import { nip19 } from "nostr-tools";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { genUserName } from "@/lib/genUserName";
import { ArrowLeft, Sunrise } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/useToast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LoginArea } from "@/components/auth/LoginArea";
import { NoteContent } from "@/components/NoteContent";
import { isDirectReplyTo, buildReplyTags } from "@/lib/nip10";

export function ThreadView() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: createEvent } = useNostrPublish();

  const [customMessage, setCustomMessage] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [gmReplies, setGmReplies] = useState<NostrEvent[]>([]);

  // Decode the note ID
  const eventId = noteId?.startsWith("note1")
    ? (nip19.decode(noteId).data as string)
    : noteId;

  // Fetch the original note
  const { data: originalNote, isLoading: isLoadingNote } = useQuery({
    queryKey: ["note", eventId],
    queryFn: async (c) => {
      if (!eventId) return null;
      const events = await nostr.query(
        [{ ids: [eventId], kinds: [1], limit: 1 }],
        { signal: c.signal },
      );
      return events[0] || null;
    },
    enabled: !!eventId,
  });

  // Real-time subscription for direct GM replies only
  useEffect(() => {
    if (!eventId) return;

    const eventMap = new Map<string, NostrEvent>();
    const controller = new AbortController();

    const runSubscription = async () => {
      try {
        for await (const msg of nostr.req(
          [{ kinds: [1], "#e": [eventId], limit: 100 }],
          { signal: controller.signal },
        )) {
          if (msg[0] === "EVENT") {
            const event = msg[2] as NostrEvent;
            // Only include direct replies (not nested/grandchild replies) that contain GM
            if (containsGM(event.content) && isDirectReplyTo(event, eventId)) {
              eventMap.set(event.id, event);
              // Sort by timestamp when updating
              const sorted = Array.from(eventMap.values()).sort(
                (a, b) => a.created_at - b.created_at,
              );
              setGmReplies(sorted);
            }
          } else if (msg[0] === "EOSE") {
            break; // Stop iterating after EOSE
          }
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("[GM Protocol] Error fetching GM replies:", error);
        }
      }
    };

    runSubscription();

    return () => {
      controller.abort();
    };
  }, [nostr, eventId]);

  useSeoMeta({
    title: "GM Thread - GM Protocol",
    description: "View and reply to GM notes",
  });

  const handleQuickGM = () => {
    if (!originalNote) return;

    setIsPublishing(true);
    createEvent(
      {
        kind: 1,
        content: "GM",
        tags: buildReplyTags(originalNote, originalNote),
      },
      {
        onSuccess: () => {
          toast({
            title: "GM sent!",
            description: "Your GM has been published to your frens.",
          });
          setIsPublishing(false);
          navigate("/");
        },
        onError: () => {
          toast({
            title: "Failed to send GM",
            description: "Something went wrong. Please try again.",
            variant: "destructive",
          });
          setIsPublishing(false);
        },
      },
    );
  };

  const handleCustomGM = () => {
    if (!originalNote || !customMessage.trim()) return;

    setIsPublishing(true);
    createEvent(
      {
        kind: 1,
        content: customMessage,
        tags: buildReplyTags(originalNote, originalNote),
      },
      {
        onSuccess: () => {
          toast({
            title: "GM sent!",
            description: "Your GM has been published to your frens.",
          });
          setCustomMessage("");
          setIsCustomMode(false);
          setIsPublishing(false);
          navigate("/");
        },
        onError: () => {
          toast({
            title: "Failed to send GM",
            description: "Something went wrong. Please try again.",
            variant: "destructive",
          });
          setIsPublishing(false);
        },
      },
    );
  };

  if (isLoadingNote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <Skeleton className="h-8 w-32 mb-6" />
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!originalNote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <Card className="border-dashed max-w-md">
          <CardContent className="py-12 px-8 text-center">
            <p className="text-muted-foreground">
              Note not found. It may have been deleted or the ID is incorrect.
            </p>
            <Button onClick={() => navigate("/")} className="mt-4">
              Back to Inbox
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Back to Inbox</span>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
                <Sunrise className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                GM Protocol
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LoginArea className="max-w-60" />
          </div>
        </div>

        {/* Original Note */}
        <OriginalNoteCard event={originalNote} />

        {/* GM Reply Actions */}
        {user && (
          <Card className="mt-6 border-2 border-amber-200 dark:border-amber-900">
            <CardContent className="pt-6">
              {!isCustomMode ? (
                <div className="flex gap-3">
                  <Button
                    onClick={handleQuickGM}
                    disabled={isPublishing}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                  >
                    {isPublishing ? "Sending..." : "Quick GM ☀️"}
                  </Button>
                  <Button
                    onClick={() => setIsCustomMode(true)}
                    disabled={isPublishing}
                    variant="outline"
                    className="flex-1"
                  >
                    Custom GM
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Type your GM message..."
                    className="min-h-[100px]"
                    disabled={isPublishing}
                  />
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCustomGM}
                      disabled={!customMessage.trim() || isPublishing}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                    >
                      {isPublishing ? "Sending..." : "Send GM ☀️"}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsCustomMode(false);
                        setCustomMessage("");
                      }}
                      disabled={isPublishing}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GM Replies Timeline */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
            GM Timeline ☀️
          </h2>

          {gmReplies.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground">
                  No GM replies yet. Be the first to GM! ☀️
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {gmReplies.map((reply) => (
                <GMReplyCard key={reply.id} event={reply} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OriginalNoteCard({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(event.pubkey);
  const profileImage = metadata?.picture;

  const timestamp = new Date(event.created_at * 1000).toLocaleString();

  return (
    <Card className="border-2 border-amber-300 dark:border-amber-800">
      <CardHeader>
        <div className="flex items-start space-x-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={profileImage} alt={displayName} />
            <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {displayName}
            </div>
            <div className="text-sm text-muted-foreground">{timestamp}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <NoteContent content={event.content} />
      </CardContent>
    </Card>
  );
}

function GMReplyCard({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(event.pubkey);
  const profileImage = metadata?.picture;

  const timestamp = new Date(event.created_at * 1000).toLocaleString();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start space-x-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={profileImage} alt={displayName} />
            <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {displayName}
            </div>
            <div className="text-sm text-muted-foreground">{timestamp}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <NoteContent content={event.content} />
      </CardContent>
    </Card>
  );
}

export default ThreadView;
